import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { posts, comments } from '../data/mockData';

const PostDetailContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PostCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

const PostMedia = styled.div`
  width: 100%;
  max-height: 500px;
  overflow: hidden;
  
  img, video {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
`;

const PostContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const GroupInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const GroupIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

const GroupName = styled(Link)`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PostDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const UserAvatar = styled(Link)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing.sm};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled(Link)`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostTime = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const UserRank = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: white;
  background: ${({ theme }) => theme.gradients.accent};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing.md};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  span {
    margin-left: ${({ theme }) => theme.spacing.xs};
  }
`;

const CommentsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const CommentsSectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  align-self: flex-end;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const CommentsList = styled.div``;

const CommentCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CommentContent = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  span {
    margin-left: ${({ theme }) => theme.spacing.xs};
  }
`;

const ReplyForm = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  display: ${({ isVisible }) => isVisible ? 'block' : 'none'};
`;

const RepliesList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-left: ${({ theme }) => theme.spacing.xl};
`;

const ReplyCard = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PostDetailPage = () => {
  const { postId } = useParams();
  const [commentText, setCommentText] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // Find the post by ID
  const post = posts.find(p => p.id === parseInt(postId)) || posts[0];

  // Find comments for this post
  const postComments = comments.filter(c => c.post.id === post.id);

  const handleLike = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
    } else {
      setLikeCount(likeCount + 1);
    }
    setLiked(!liked);
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (commentText.trim()) {
      // In a real app, this would send the comment to the server
      alert(`Comment submitted: ${commentText}`);
      setCommentText('');
    }
  };

  const handleReplySubmit = (commentId) => {
    if (replyText.trim()) {
      // In a real app, this would send the reply to the server
      alert(`Reply to comment ${commentId} submitted: ${replyText}`);
      setReplyText('');
      setReplyingTo(null);
    }
  };

  const toggleReplyForm = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setReplyText('');
  };

  return (
    <PostDetailContainer>
      <PostCard>
        {post.media && (
          <PostMedia>
            {post.mediaType === 'image' ? (
              <img src={post.media} alt={post.title} />
            ) : (
              <video src={post.media} poster={post.thumbnail} controls />
            )}
          </PostMedia>
        )}

        <PostContent>
          <GroupInfo>
            <GroupIcon>{post.group.icon}</GroupIcon>
            <GroupName to={`/group/${post.group.id}`}>{post.group.name}</GroupName>
          </GroupInfo>

          <PostTitle>{post.title}</PostTitle>
          <PostDescription>{post.description}</PostDescription>

          <PostMeta>
            <UserAvatar to={`/profile/${post.user.username}`}>
              <img src={post.user.avatar} alt={post.user.name} />
            </UserAvatar>
            <UserInfo>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <UserName to={`/profile/${post.user.username}`}>{post.user.name}</UserName>
                <UserRank>{post.user.rank}</UserRank>
              </div>
              <PostTime>{post.timeAgo}</PostTime>
            </UserInfo>
          </PostMeta>

          <PostActions>
            <ActionButton active={liked} onClick={handleLike}>
              {liked ? '❤️' : '🤍'} <span>{post.likes + (liked ? 1 : 0)}</span>
            </ActionButton>
            <ActionButton>
              💬 <span>{post.comments}</span>
            </ActionButton>
            <ActionButton>
              🔄 <span>Share</span>
            </ActionButton>
            <ActionButton>
              🔖 <span>Save</span>
            </ActionButton>
          </PostActions>
        </PostContent>
      </PostCard>

      <CommentsSection>
        <CommentsSectionTitle>Comments ({post.comments})</CommentsSectionTitle>

        <CommentForm onSubmit={handleCommentSubmit}>
          <CommentTextarea
            placeholder="Add a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
          <SubmitButton type="submit">Post Comment</SubmitButton>
        </CommentForm>

        <CommentsList>
          {postComments.map(comment => (
            <CommentCard key={comment.id}>
              <CommentHeader>
                <UserAvatar to={`/profile/${comment.user.username}`}>
                  <img src={comment.user.avatar} alt={comment.user.name} />
                </UserAvatar>
                <UserInfo>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <UserName to={`/profile/${comment.user.username}`}>{comment.user.name}</UserName>
                    <UserRank>{comment.user.rank}</UserRank>
                  </div>
                  <PostTime>{new Date(comment.timestamp).toLocaleString()}</PostTime>
                </UserInfo>
              </CommentHeader>

              <CommentContent>{comment.content}</CommentContent>

              <CommentActions>
                <CommentButton>
                  ❤️ <span>{comment.likes}</span>
                </CommentButton>
                <CommentButton onClick={() => toggleReplyForm(comment.id)}>
                  💬 <span>Reply</span>
                </CommentButton>
              </CommentActions>

              <ReplyForm isVisible={replyingTo === comment.id}>
                <CommentTextarea
                  placeholder="Write a reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <SubmitButton onClick={() => handleReplySubmit(comment.id)}>Post Reply</SubmitButton>
              </ReplyForm>

              {comment.replies && comment.replies.length > 0 && (
                <RepliesList>
                  {comment.replies.map(reply => (
                    <ReplyCard key={reply.id}>
                      <CommentHeader>
                        <UserAvatar to={`/profile/${reply.user.username}`}>
                          <img src={reply.user.avatar} alt={reply.user.name} />
                        </UserAvatar>
                        <UserInfo>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <UserName to={`/profile/${reply.user.username}`}>{reply.user.name}</UserName>
                            <UserRank>{reply.user.rank}</UserRank>
                          </div>
                          <PostTime>{new Date(reply.timestamp).toLocaleString()}</PostTime>
                        </UserInfo>
                      </CommentHeader>

                      <CommentContent>{reply.content}</CommentContent>

                      <CommentActions>
                        <CommentButton>
                          ❤️ <span>{reply.likes}</span>
                        </CommentButton>
                      </CommentActions>
                    </ReplyCard>
                  ))}
                </RepliesList>
              )}
            </CommentCard>
          ))}
        </CommentsList>
      </CommentsSection>
    </PostDetailContainer>
  );
};

export default PostDetailPage;
